import React from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Tag } from "../../../design-system";
import {
    EvaluationGoal,
    SupplierType,
    SUPPLIER_TYPE_LABELS,
    EVALUATION_GOAL_LABELS,
} from "../../../types/BuyerBriefTypes";
import { getReviewSections } from "../reviewSections";
import styles from "../BuyerBriefWizard.module.less";

interface Props {
    supplierName: string;
    supplierWebsite: string;
    vendorKey?: string;
    supplierType: SupplierType | undefined;
    evaluationGoal: EvaluationGoal | undefined;
    selectedTopics: string[];
    onEditStep: (step: number) => void;
}

export const BriefReviewStep: React.FC<Props> = ({
    supplierName,
    supplierWebsite,
    vendorKey,
    supplierType,
    evaluationGoal,
    selectedTopics,
    onEditStep,
}) => {
    const sections = getReviewSections(supplierType, selectedTopics);

    return (
        <div>
            <div className={styles.sectionTitle}>Review & Confirm</div>
            <div className={styles.sectionSub}>
                Check the supplier details and research scope before generating the brief.
            </div>

            <div className={styles.reviewSection}>
                <div className={styles.reviewSectionHeader}>
                    <span className={styles.topicCategory}>Supplier</span>
                    <Button type="link" size="small" onClick={() => onEditStep(0)}>
                        Edit
                    </Button>
                </div>
                <div className={styles.reviewFacts}>
                    <div className={styles.reviewFact}>
                        <span className={styles.reviewFactLabel}>Supplier Name</span>
                        <span className={styles.reviewFactValue}>{supplierName}</span>
                    </div>
                    <div className={styles.reviewFact}>
                        <span className={styles.reviewFactLabel}>Website</span>
                        <span className={styles.reviewFactValue}>{supplierWebsite || "—"}</span>
                    </div>
                    <div className={styles.reviewFact}>
                        {/* Only set when the name came from the typeahead. Surfacing it
                            here is how the analyst can tell the brief will be findable
                            from that vendor's deals. */}
                        <span className={styles.reviewFactLabel}>Linked Vendor</span>
                        <span className={styles.reviewFactValue}>
                            {vendorKey ? supplierName : "Not linked"}
                        </span>
                    </div>
                    <div className={styles.reviewFact}>
                        <span className={styles.reviewFactLabel}>Spend Category</span>
                        <span className={styles.reviewFactValue}>
                            {supplierType ? SUPPLIER_TYPE_LABELS[supplierType] : "—"}
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.reviewSection}>
                <div className={styles.reviewSectionHeader}>
                    <span className={styles.topicCategory}>Evaluation Goal</span>
                    <Button type="link" size="small" onClick={() => onEditStep(1)}>
                        Edit
                    </Button>
                </div>
                <div className={styles.reviewFacts}>
                    <div className={styles.reviewFact}>
                        <span className={styles.reviewFactValue}>
                            {evaluationGoal ? EVALUATION_GOAL_LABELS[evaluationGoal] : "—"}
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.reviewSectionHeader}>
                <span className={styles.topicCategory}>Research Areas</span>
                <Button type="link" size="small" onClick={() => onEditStep(2)}>
                    Edit topics
                </Button>
            </div>
            {sections.map((section) => (
                <div key={section.title} className={styles.reviewSection}>
                    <div className={styles.reviewSectionHeader}>
                        <span className={styles.reviewSectionTitle}>{section.title}</span>
                        <span className={styles.reviewCount}>
                            {section.included.length} of{" "}
                            {section.included.length + section.excluded.length} included
                        </span>
                    </div>
                    <div className={styles.reviewTagWrap}>
                        {section.included.map((topic) => (
                            <Tag
                                key={topic.id}
                                className={styles.tagIncluded}
                                icon={<CheckOutlined />}
                            >
                                {topic.label}
                            </Tag>
                        ))}
                        {section.excluded.map((topic) => (
                            <Tag
                                key={topic.id}
                                className={styles.tagExcluded}
                                icon={<CloseOutlined />}
                            >
                                {topic.label}
                            </Tag>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
